import { URLS } from '../config/app-config';
import { secureFetch } from './secureFetch'

const base = URLS.apiBase.replace(/\/$/, '')
const exitStates = new Set(['completed', 'failed', 'stopped'])
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const waitForJob = async (job, { onProgress, signal } = {}) => {
    const id = job.groupId || job.jobId
    const type = job.groupId ? 'group' : 'job'

    while (true) {
        if (signal?.aborted) throw new Error('Job aborted')

        const response = await secureFetch(job.groupId
            ? `${base}/jobs/groups/${id}/info`
            : `${base}/jobs/${id}`)

        if (response.status === 404) {
            console.log(`${type} '${id}' not found or was reaped by the job manager.`)
            return
        }

        if (!response.ok) throw new Error(`Job polling failed: ${response.status}`)

        const data = await response.json()
        const jobs = job.groupId ? data.jobs : [data]

        if (jobs.length && jobs.every(({ status }) => exitStates.has(status.trim().toLowerCase()))) {
            console.log(`${type} '${id}' has exited.`)
            return
        }

        onProgress?.(jobs.reduce((highest, { progress }) =>
            typeof progress === 'number' ? Math.max(highest, progress) : highest, 0))

        await sleep(1000)
    }
}

export async function awaitJobCompletion({
    url,
    body = {},
    jobRef,
    onProgress,
    signal,
    requireAuth = false
}) {
    const response = await secureFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    }, requireAuth)

    if (!response.ok) throw new Error(`Job creation failed: ${response.status}`)

    const data = await response.json()
    const key = data.groupId ? 'groupId' : 'jobId'
    const id = typeof data[key] === 'object' ? data[key][key] : data[key]

    if (!id) throw new Error('No job or group id returned from endpoint')

    const job = { [key]: id }
    if (jobRef) jobRef.current = job

    try {
        await waitForJob(job, { onProgress, signal })
    } catch {
        return null
    }

    onProgress?.(100)
    return data
}

export const generatePdf = async ({
    articleIds,
    selectedRelease,
    jobRef,
    onProgress,
    signal
}) => {
    const data = await awaitJobCompletion({
        url: `${base}/print`,
        body: {
            article_ids: articleIds,
            ...selectedRelease,
        },
        jobRef,
        onProgress,
        signal
    })

    if (!data?.pdfUrl) throw new Error('No PDF URL was created')
    return data.pdfUrl
}

export const generateSummary = async ({
    prompt,
    articleIds,
    selectedRelease,
    jobRef,
    onProgress,
    signal
}) => {
    const data = await awaitJobCompletion({
        url: `${base}/llm`,
        body: {
            prompt,
            article_ids: articleIds,
            ...selectedRelease,
        },
        jobRef,
        onProgress,
        signal,
        requireAuth: true
    })

    if (!data?.answerUrl) throw new Error('No LLM summary was generated')
    return data.answerUrl
}
